import { useAppContext } from "@/context/AppContext";
import {
  Address,
  AssetId,
  CredentialType,
  Metadata,
  MetadatumMap,
  Metadatum,
  TokenMap,
  TransactionId,
  TransactionInput,
  TransactionOutput,
  TransactionUnspentOutput,
  Value,
} from "@blaze-cardano/core";
import { TPlutusData, Type, serialize } from "@blaze-cardano/data";
import { ensureEphemeralWallet, signWithEphemeralWallet } from "@/lib/ephemeralWallet";
import { blake2b } from "@noble/hashes/blake2";
import { AssetAmount } from "@sundaeswap/asset";
import { useWalletObserver } from "@sundaeswap/wallet-lite";
import { DefaultError, useMutation } from "@tanstack/react-query";
import { useDepositWithBody } from "./useDepositWithBody";
import { useHydrozoaHeadInfo } from "./useHydrozoaScriptAddress";
import { useGummiwormContext } from "@/context/GummiwormContext";
import { Event } from "@module-bindings/types";
import { useReducer } from "spacetimedb/react";
import { reducers } from "@module-bindings";
import { useDepositTxs } from "@/context/GummiwormContext/hooks/useDepositTxs";

const CredentialSchema = Type.Union([
  Type.Object(
    { PubKeyCredential: Type.Object({ hash: Type.String() }, { ctor: 0 }) },
    { ctor: 0 },
  ),
  Type.Object(
    { ScriptCredential: Type.Object({ hash: Type.String() }, { ctor: 1 }) },
    { ctor: 1 },
  ),
]);

const PlutusAddressSchema = Type.Object(
  {
    paymentCredential: CredentialSchema,
    stakeCredential: Type.Optional(
      Type.Union([
        Type.Object(
          { Inline: CredentialSchema },
          { ctor: 0 },
        ),
        Type.Object(
          {
            Pointer: Type.Tuple([
              Type.Object({
                slotNumber: Type.BigInt(),
                transactionIndex: Type.BigInt(),
                certificateIndex: Type.BigInt(),
              }),
            ]),
          },
          { ctor: 1 },
        ),
      ]),
    ),
  },
  { ctor: 0 },
);

const RefundInstructionsSchema = Type.Object(
  {
    Address: PlutusAddressSchema,
    MbDatum: Type.Optional(TPlutusData),
    RefundStart: Type.BigInt(),
  },
  { additionalProperties: false, ctor: 0 },
);

const DepositUtxoDatumSchema = Type.Object(
  {
    RefundInstructions: RefundInstructionsSchema,
  },
  { additionalProperties: false, ctor: 0 },
);

const toSeconds = (value: number) => Math.floor(value / 1000);

export const useHandleDepositRequest = () => {
  const {
    state: { blaze, provider },
  } = useAppContext();
  const { mainAddress } = useWalletObserver();
  const { depositTxs, addDepositTx, updateDepositTx } = useDepositTxs();
  const headInfo = useHydrozoaHeadInfo();
  const { mutateAsync: deposit } = useDepositWithBody();
  const saveEvent = useReducer(reducers.saveEvents);

  return useMutation<unknown, DefaultError, { depositAmounts: AssetAmount[] }>({
    mutationKey: [
      "deposit",
      "register",
      blaze,
      provider,
      mainAddress,
      headInfo,
      saveEvent,
      addDepositTx,
    ],
    mutationFn: async ({ depositAmounts }) => {
      if (!blaze || !mainAddress || !provider) {
        throw new Error(
          "Connect a Cardano wallet before registering a deposit.",
        );
      }

      if (!headInfo) {
        throw new Error(
          "Sugar Rush is offline right now. Please try again later.",
        );
      }

      const { headAddress: scriptAddress, headId, multisigRegimeUtxo, submissionDurationSeconds, refundStartOffsetSeconds, absorptionStartOffsetSeconds } = headInfo;

      const refundAddress = Address.fromBech32(mainAddress);
      const props = refundAddress.getProps();
      const paymentHash = props.paymentPart?.hash;
      if (!paymentHash) {
        throw new Error("Could not extract payment credential from address.");
      }
      const paymentCredential =
        props.paymentPart!.type === CredentialType.ScriptHash
          ? { ScriptCredential: { hash: paymentHash } }
          : { PubKeyCredential: { hash: paymentHash } };
      // Force enterprise address (no staking credential) for now
      const stakeCredential = undefined;

      const validityStart = toSeconds(Date.now() - 1_000); // 30s for hydrozoa to validate the transaction
      const validityEnd = validityStart + 31;
      const refundStart = validityEnd + refundStartOffsetSeconds;
      const txValidUntil = validityEnd + submissionDurationSeconds;
      const datum = serialize(DepositUtxoDatumSchema, {
        RefundInstructions: {
          Address: { paymentCredential, stakeCredential },
          MbDatum: undefined,
          RefundStart: BigInt(refundStart * 1_000),
        },
      });

      // Get the ephemeral key so we can register it as a delegated key
      const ephemeral = await ensureEphemeralWallet(mainAddress);
      const ephemeralKeyHash = blake2b(
        Buffer.from(ephemeral.publicKey, "hex"),
        { dkLen: 28 },
      ).toHex();

      const l2Payload = {
        accountId: Address.fromBech32(mainAddress).getProps().paymentPart?.hash,
        delegatedKey: ephemeralKeyHash,
        evacuationDestination: undefined,
      };

      // Build deposit metadata (label 4937)
      const l2PayloadHash = blake2b(
        Buffer.from(JSON.stringify(l2Payload)),
        { dkLen: 32 },
      ).toHex();

      const depositDetails = new MetadatumMap();
      depositDetails.insert(
        Metadatum.newText("depositIx"),
        Metadatum.newInteger(0n),
      );
      depositDetails.insert(
        Metadatum.newText("depositFee"),
        Metadatum.newInteger(0n),
      );
      depositDetails.insert(
        Metadatum.newText("l2PayloadHash"),
        Metadatum.newText(l2PayloadHash),
      );

      const headIdMap = new MetadatumMap();
      headIdMap.insert(
        Metadatum.newText(headId),
        Metadatum.newMap(depositDetails),
      );

      const depositMap = new MetadatumMap();
      depositMap.insert(
        Metadatum.newText("Deposit"),
        Metadatum.newMap(headIdMap),
      );

      const metadata = new Metadata(
        new Map([[4937n, Metadatum.newMap(depositMap)]]),
      );

      // Build reference input for multisig regime UTxO
      // We construct a minimal UTxO rather than resolving via provider,
      // because the regime UTxO contains a native script ref that Blaze
      // can't deserialize. Only the input ref matters for reference inputs.
      const regimeInput = new TransactionInput(
        TransactionId(multisigRegimeUtxo.transaction_id) as any,
        BigInt(multisigRegimeUtxo.index),
      );
      const regimeOutput = new (TransactionOutput as any)(
        Address.fromBech32(scriptAddress),
        new Value(0n),
      );
      const regimeUtxo = new TransactionUnspentOutput(regimeInput, regimeOutput);

      // Build Value from all deposit amounts
      let lovelace = 0n;
      const tokens = new Map() as TokenMap;
      for (const amt of depositAmounts) {
        if (amt.metadata.assetId === "ada.lovelace") {
          lovelace += amt.amount;
        } else {
          // Convert sundae assetId format "policyId.assetNameHex" to blaze AssetId
          const [policyId, assetName] = amt.metadata.assetId.split(".");
          const blazeAssetId = AssetId(policyId + (assetName ?? ""));
          tokens.set(blazeAssetId, (tokens.get(blazeAssetId) ?? 0n) + amt.amount);
        }
      }
      const depositValue = new Value(lovelace, tokens.size > 0 ? tokens : undefined);

      // lockAssets is called first so the deposit is always output index 0
      const tx = await blaze
        .newTransaction()
        .setFeePadding(1000n)
        .setValidUntil(provider.unixToSlot(txValidUntil * 1_000))
        .lockAssets(
          Address.fromBech32(scriptAddress),
          depositValue,
          datum,
        )
        .addReferenceInput(regimeUtxo)
        .setMetadata(metadata)
        .complete();

      const depositPayload = {
        l1Payload: tx.toCbor(),
        l2Payload: Buffer.from(JSON.stringify(l2Payload)).toHex(),
      };

      const bodyHash = blake2b(Buffer.from(JSON.stringify(depositPayload)), {
        dkLen: 32,
      }).toHex();
      const header = {
        headId,
        validityStart,
        validityEnd,
        bodyHash,
      };

      const { publicKey, signature } = await signWithEphemeralWallet(
        mainAddress,
        Buffer.from(JSON.stringify(header)),
      );

      const body = {
        header,
        deposit: depositPayload,
        userVk: publicKey,
        signature,
      };

      const result = await deposit(body);

      const id = BigInt(result.requestId);

      // Estimate when the deposit will be absorbed:
      // validityEnd + absorptionStartOffsetSeconds
      const estimatedAbsorptionTime = absorptionStartOffsetSeconds
        ? validityEnd + absorptionStartOffsetSeconds
        : undefined;

      // Register the deposit before signing so we can track status
      // even if the refund tx arrives before signing completes.
      if (!depositTxs.has(id.toString())) {
        addDepositTx(id, {
          cbor: "",
          hash: "",
          refundCbor: "",
          status: "new",
          estimatedAbsorptionTime,
        });
      }

      const signedTx = await blaze.signTransaction(tx);
      const cbor = signedTx.toCbor();

      // Save the signed CBOR. The reconciler in GummiwormProvider will
      // detect cbor + refundCbor are both present and handle submission.
      updateDepositTx(id, { cbor });

      return result;
    },
  });
};
